// frontend/src/pages/public/SolutionTemplate.jsx
import React from "react";
import { useParams, Navigate, Link } from "react-router-dom";
import { CheckCircleIcon } from "@heroicons/react/24/solid";
import Seo from "../../marketing/Seo";
import { Container, Section, Eyebrow, Badge, Button } from "../../marketing/ui";
import { CTASection } from "../../marketing/blocks";
import { getSolution } from "../../marketing/data/solutions";

export default function SolutionTemplate() {
  const { slug } = useParams();
  const s = getSolution(slug);
  if (!s) return <Navigate to="/solutions" replace />;

  const origin = typeof window !== "undefined" ? window.location.origin : "";

  return (
    <>
      <Seo
        title={`${s.name} — ${s.headline}`}
        description={s.subhead}
        path={`/solutions/${s.slug}`}
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Solutions", item: `${origin}/solutions` },
            { "@type": "ListItem", position: 2, name: s.name, item: `${origin}/solutions/${s.slug}` },
          ],
        }}
      />

      {/* Hero */}
      <div className="bg-gradient-to-b from-brand-50/60 to-white">
        <Container className="py-16 sm:py-20">
          <nav className="text-sm text-gray-500">
            <Link to="/solutions" className="hover:text-brand-700">Solutions</Link>
            <span className="px-2">/</span>
            <span className="text-gray-700">{s.name}</span>
          </nav>
          <div className="mt-6 max-w-3xl">
            <Eyebrow>{s.eyebrow}</Eyebrow>
            <h1 className="mt-2 text-4xl font-bold tracking-tight text-gray-900 sm:text-5xl">
              {s.headline}
            </h1>
            <p className="mt-5 text-lg leading-8 text-gray-600">{s.subhead}</p>
            <div className="mt-8 flex flex-col gap-3 sm:flex-row">
              <Button to="/register" size="lg">Get started free</Button>
              <Button to="/pricing" size="lg" variant="secondary">See pricing</Button>
            </div>
          </div>
        </Container>
      </div>

      {/* Problem + stat */}
      <Section>
        <div className="grid items-center gap-10 lg:grid-cols-2">
          <div>
            <h2 className="text-2xl font-bold tracking-tight text-gray-900">{s.problem.title}</h2>
            <p className="mt-4 text-lg leading-8 text-gray-600">{s.problem.body}</p>
          </div>
          {s.stat && (
            <div className="rounded-3xl bg-brand-600 p-10 text-center text-white">
              <p className="text-5xl font-bold tracking-tight">{s.stat.value}</p>
              <p className="mt-2 text-brand-100">{s.stat.label}</p>
            </div>
          )}
        </div>
      </Section>

      {/* Practical ideas */}
      <Section muted>
        <div className="mx-auto max-w-2xl text-center">
          <Eyebrow>Practical ideas</Eyebrow>
          <h2 className="mt-2 text-3xl font-bold tracking-tight text-gray-900">
            Ways to use QR codes in {s.name.toLowerCase()}
          </h2>
        </div>
        <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {s.ideas.map((idea) => (
            <div key={idea.title} className="rounded-2xl bg-white p-6 ring-1 ring-gray-200">
              <CheckCircleIcon className="h-7 w-7 text-accent-600" aria-hidden="true" />
              <h3 className="mt-3 text-lg font-semibold text-gray-900">{idea.title}</h3>
              <p className="mt-2 text-sm leading-6 text-gray-600">{idea.body}</p>
            </div>
          ))}
        </div>
      </Section>

      {/* QR types + features */}
      <Section>
        <div className="grid gap-12 lg:grid-cols-2">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">Code types that fit</h2>
            <div className="mt-4 flex flex-wrap gap-2">
              {s.qrTypes.map((t) => (
                <Badge key={t} tone="gray">{t}</Badge>
              ))}
            </div>
            <Link to="/qr-types" className="mt-6 inline-block text-sm font-semibold text-brand-700 hover:text-brand-800">
              Browse all QR code types →
            </Link>
          </div>
          <div>
            <h2 className="text-xl font-semibold text-gray-900">QRFlow features that help</h2>
            <ul className="mt-4 space-y-4">
              {s.features.map((f) => (
                <li key={f.title}>
                  <p className="font-medium text-gray-900">{f.title}</p>
                  <p className="mt-1 text-sm text-gray-600">{f.body}</p>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </Section>

      <CTASection
        title={`Start using QR codes for ${s.name.toLowerCase()} today`}
        subtitle="Free for 5 dynamic codes. No credit card, change destinations any time."
        primary={{ to: "/register", label: "Get started free" }}
        secondary={{ to: "/solutions", label: "Explore other industries" }}
      />
    </>
  );
}
